import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Row, Col, Button, Modal } from 'react-bootstrap';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getTempleReport } from '../../../../redux/slices/templeSlice';
import DataTable from 'react-data-table-component';
import { FilePdf, FileXls, MagnifyingGlass, Calendar, Package, CaretDown, CaretUp, ArrowsClockwise } from "@phosphor-icons/react";
import Select from 'react-select';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Eye } from 'feather-icons-react/build/IconComponents';

const TempleReports = () => {
  const dispatch = useDispatch();
  const { templeReport, loading } = useSelector(state => state.templeSlice);
  const [filteredTemples, setFilteredTemples] = useState([]);
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedTemple, setSelectedTemple] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedTempleForPackages, setSelectedTempleForPackages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('temple_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const paginationPerPage = 10;

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: '0.375rem',
      boxShadow: 'none',
      borderColor: '#ced4da',
      '&:hover': {
        borderColor: '#86b7fe'
      },
      height: '50px'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? 'rgba(13, 110, 253, 0.1)' : null,
      color: state.isSelected ? 'white' : 'inherit'
    })
  };

  useEffect(() => {
    dispatch(getTempleReport());
  }, [dispatch]);

  useEffect(() => {
    if (!templeReport) return;
    
    let filtered = [...templeReport];

    if (startDate && endDate) {
      filtered = filtered.filter(temple => {
        return temple.booking_history.some(booking => {
          const bookingStartDate = moment(booking.start_date);
          return bookingStartDate.isBetween(moment(startDate), moment(endDate), 'day', '[]');
        });
      });
    }

    if (selectedState) {
      filtered = filtered.filter(temple => temple.temple_state === selectedState.value);
    }

    if (selectedCity) {
      filtered = filtered.filter(temple => temple.temple_city === selectedCity.value);
    }

    if (selectedTemple) {
      filtered = filtered.filter(temple => temple.temple_name === selectedTemple.value);
    }

    setFilteredTemples(filtered);
    setCurrentPage(1);
  }, [templeReport, selectedState, selectedCity, selectedTemple, startDate, endDate]);

  const getStateOptions = () => {
    if (!templeReport) return [];
    const states = [...new Set(templeReport.map(temple => temple.temple_state))];
    return states.map(state => ({ label: state, value: state }));
  };

  const getCityOptions = () => {
    if (!templeReport) return [];
    const cities = [...new Set(templeReport.map(temple => temple.temple_city))];
    return cities.map(city => ({ label: city, value: city }));
  };

  const getTempleOptions = () => {
    if (!templeReport) return [];
    const temples = [...new Set(templeReport.map(temple => temple.temple_name))];
    return temples.map(name => ({ label: name, value: name }));
  };

  const handleSort = (column) => {
    const newDirection = sortField === column && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(column);
    setSortDirection(newDirection);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <CaretUp size={16} /> : <CaretDown size={16} />;
  };

  const handlePackageClick = (temple) => {
    setSelectedTempleForPackages(temple);
    setShowPackageModal(true);
  };

  const getUniquePackages = (temple) => {
    if (!temple?.booking_history) return [];
    return [...new Set(temple.booking_history.map(booking => booking.package_name))];
  };

  const columns = [
    {
      name: <div className="d-flex align-items-center cursor-pointer" onClick={() => handleSort('temple_name')}>
              Temple Name <SortIcon field="temple_name" />
            </div>,
      selector: row => row.temple_name,
      sortable: true,
      wrap: true
    },
    {
      name: <div className="d-flex align-items-center cursor-pointer" onClick={() => handleSort('temple_state')}>
              State <SortIcon field="temple_state" />
            </div>,
      selector: row => row.temple_state,
      sortable: true
    },
    {
      name: <div className="d-flex align-items-center cursor-pointer" onClick={() => handleSort('temple_city')}>
              City <SortIcon field="temple_city" />
            </div>,
      selector: row => row.temple_city,
      sortable: true
    },
    {
      name: "Location",
      selector: row => row.temple_location,
      wrap: true
    },
    {
      name: "Total Bookings",
      selector: row => {
        const validBookings = row.booking_history.filter(booking => {
          const bookingStartDate = moment(booking.start_date);
          return bookingStartDate.isBetween(moment(startDate), moment(endDate), 'day', '[]');
        });
        return validBookings.length;
      },
      sortable: true,
      cell: row => {
        const validBookings = row.booking_history.filter(booking => {
          const bookingStartDate = moment(booking.start_date);
          return bookingStartDate.isBetween(moment(startDate), moment(endDate), 'day', '[]');
        });
        return <span className="fw-bold">{validBookings.length}</span>;
      }
    },
    {
      name: "Package Details",
      cell: row => (
        <Button 
          variant="outline-primary" 
          size="sm"
          onClick={() => handlePackageClick(row)}
        >
         <Eye size={16} />
        </Button>
      )
    }
  ];

  const calculateStats = (data) => {
    if (!data || data.length === 0) return {
      totalTemples: 0,
      totalBookings: 0,
      defaultBookings: 0,
      customizedBookings: 0
    };

    const totalTemples = data.length;
    
    const totalBookings = data.reduce((sum, temple) => {
      const validBookings = temple.booking_history.filter(booking => {
        const bookingStartDate = moment(booking.start_date);
        return bookingStartDate.isBetween(moment(startDate), moment(endDate), 'day', '[]');
      });
      return sum + validBookings.length;
    }, 0);

    // Count default bookings within date range
    const defaultBookings = data.reduce((sum, temple) => {
      const validDefaultBookings = temple.booking_history.filter(booking => {
        const bookingStartDate = moment(booking.start_date);
        return bookingStartDate.isBetween(moment(startDate), moment(endDate), 'day', '[]') 
          && booking.package_type === 'default';
      });
      return sum + validDefaultBookings.length;
    }, 0);

    // Count customized bookings within date range
    const customizedBookings = data.reduce((sum, temple) => {
      const validCustomBookings = temple.booking_history.filter(booking => {
        const bookingStartDate = moment(booking.start_date);
        return bookingStartDate.isBetween(moment(startDate), moment(endDate), 'day', '[]')
          && booking.package_type === 'customized';
      });
      return sum + validCustomBookings.length;
    }, 0);

    return {
      totalTemples,
      totalBookings,
      defaultBookings,
      customizedBookings
    };
  };

  const resetFilters = () => {
    setStartDate(moment().startOf('month').format('YYYY-MM-DD'));
    setEndDate(moment().endOf('month').format('YYYY-MM-DD'));
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedTemple(null);
    setSortField('temple_name');
    setSortDirection('asc');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const stats = calculateStats(filteredTemples);
    
    doc.setFont('helvetica');
    doc.setFontSize(16);
    doc.text('Temple Reports', 105, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${moment().format('DD/MM/YYYY')}`, 14, 25);
    
    // Summary section
    doc.setFontSize(12);
    doc.text(`Total Temples: ${stats.totalTemples}`, 14, 35);
    doc.text(`Total Bookings: ${stats.totalBookings}`, 14, 42);
    doc.text(`Default Bookings: ${stats.defaultBookings}`, 14, 49);
    doc.text(`Customized Bookings: ${stats.customizedBookings}`, 14, 56);
    
    const tableData = filteredTemples.map(temple => [
      temple.temple_name,
      temple.temple_state,
      temple.temple_city,
      temple.temple_location,
      temple.statistics.total_bookings.toString(),
      getUniquePackages(temple).join(', ')
    ]);
    
    autoTable(doc, {
      head: [['Temple Name', 'State', 'City', 'Location', 'Total Bookings', 'Packages']],
      body: tableData,
      startY: 70,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [13, 110, 253] },
      columnStyles: {
        0: { cellWidth: 40 },
        3: { cellWidth: 50 },
        5: { cellWidth: 40 }
      }
    });
    
    doc.save(`Temple_Reports_${moment().format('YYYY-MM-DD')}.pdf`);
  };

  const exportToExcel = () => {
    const stats = calculateStats(filteredTemples);
    
    const summaryData = [
      ['Temple Reports'],
      [`Generated on: ${moment().format('DD/MM/YYYY')}`],
      [''],
      ['Summary'],
      ['Total Temples', stats.totalTemples],
      ['Total Bookings', stats.totalBookings],
      ['Default Bookings', stats.defaultBookings],
      ['Customized Bookings', stats.customizedBookings],
      [''],
      ['Temple Details']
    ];

    const tableData = [
      ['Temple Name', 'State', 'City', 'Location', 'Total Bookings', 'Packages'],
      ...filteredTemples.map(temple => [
        temple.temple_name,
        temple.temple_state,
        temple.temple_city,
        temple.temple_location,
        temple.statistics.total_bookings,
        getUniquePackages(temple).join(', ')
      ])
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([...summaryData, ...tableData]);

    // Set column widths
    const wscols = [
      { wch: 40 }, // Temple Name
      { wch: 15 }, // State
      { wch: 15 }, // City
      { wch: 50 }, // Location
      { wch: 15 }, // Total Bookings
      { wch: 40 }, // Packages
    ];
    ws['!cols'] = wscols;

    XLSX.utils.book_append_sheet(wb, ws, 'Temple Report');
    XLSX.writeFile(wb, `Temple_Reports_${moment().format('YYYY-MM-DD')}.xlsx`);
  };

  const stats = calculateStats(filteredTemples);

  return (
    <Container fluid className="p-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Temple Reports</h4>
            <div className="d-flex">
              <Button
                variant="outline-secondary"
                className="me-2 d-flex align-items-center"
                onClick={resetFilters}
              >
                <ArrowsClockwise size={16} className="me-1" /> Reset Filters
              </Button>
              <Button
                variant="outline-primary"
                className="icon-button me-2"
                onClick={exportToExcel}
                disabled={filteredTemples.length === 0}
                title="Export to Excel"
              >
                <FileXls size={20} className="me-1" /> Excel
              </Button>
              <Button
                variant="outline-danger"
                className="icon-button"
                onClick={exportToPDF}
                disabled={filteredTemples.length === 0}
                title="Export to PDF"
              >
                <FilePdf size={20} className="me-1" /> PDF
              </Button>
            </div>
          </div>

          <Card className="bg-light mb-4">
            <Card.Body>
              <h5 className="mb-3">Filters</h5>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <Calendar size={16} className="me-1" /> Start Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="form-control"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <Calendar size={16} className="me-1" /> End Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="form-control"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <MagnifyingGlass size={16} className="me-1" /> Temple Name
                    </Form.Label>
                    <Select
                      isClearable
                      value={selectedTemple}
                      onChange={setSelectedTemple}
                      options={getTempleOptions()}
                      placeholder="Search temple..."
                      styles={customSelectStyles}
                      isSearchable={true}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <Package size={16} className="me-1" /> State
                    </Form.Label>
                    <Select
                      isClearable
                      value={selectedState}
                      onChange={setSelectedState}
                      options={getStateOptions()}
                      placeholder="Select State"
                      styles={customSelectStyles}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                      <Package size={16} className="me-1" /> City
                    </Form.Label>
                    <Select
                      isClearable
                      value={selectedCity}
                      onChange={setSelectedCity}
                      options={getCityOptions()}
                      placeholder="Select City"
                      styles={customSelectStyles}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center h-100 border-0 shadow-sm stats-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Total Temples</h6>
                  <h3 className="mb-0 text-primary">{stats.totalTemples}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100 border-0 shadow-sm stats-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Total Bookings</h6>
                  <h3 className="mb-0 text-primary">{stats.totalBookings}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100 border-0 shadow-sm stats-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Default Bookings</h6>
                  <h3 className="mb-0 text-primary">{stats.defaultBookings}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center h-100 border-0 shadow-sm stats-card">
                <Card.Body>
                  <h6 className="text-muted mb-2">Custom Bookings</h6>
                  <h3 className="mb-0 text-primary">{stats.customizedBookings}</h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <DataTable
                columns={columns}
                data={filteredTemples}
                pagination
                paginationPerPage={paginationPerPage}
                highlightOnHover
                pointerOnHover
                progressPending={loading}
                noDataComponent={
                  <div className="p-5 text-center text-muted">
                    <p className="mb-0">No temple records found for the selected filters</p>
                  </div>
                }
                progressComponent={
                  <div className="w-100 d-flex p-4">
                    <div className="spinner-border text-primary mx-auto" role="status"></div>
                  </div>
                }
                customStyles={{
                    headRow: {
                        style: {
                            backgroundColor: '#f8f9fa',
                            borderTopStyle: 'none',
                            borderBottomStyle: 'solid',
                            borderBottomWidth: '1px',
                            borderBottomColor: '#e9ecef',
                        },
                    },
                    headCells: {
                        style: {
                            fontWeight: '600',
                            color: '#495057',
                            paddingLeft: '16px',
                            paddingRight: '16px',
                        },
                    },
                    rows: {
                        style: {
                            minHeight: '55px',
                            fontSize: '14px',
                        },
                    },
                    cells: {
                        style: {
                            paddingLeft: '16px',
                            paddingRight: '16px',
                        },
                    },
                }}
              />
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      <Modal
        show={showPackageModal}
        onHide={() => setShowPackageModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Package List - {selectedTempleForPackages?.temple_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="package-list">
            {getUniquePackages(selectedTempleForPackages).map((packageName, index) => (
              <div key={index} className="package-item p-3 mb-2 bg-light rounded">
                {packageName}
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPackageModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .stats-card {
          transition: transform 0.2s;
        }
        .stats-card:hover {
          transform: translateY(-5px);
          background-color: #f8f9fa;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .package-list {
          max-height: 400px;
          overflow-y: auto;
        }
        .package-item {
          transition: background-color 0.2s;
        }
        .package-item:hover {
          background-color: #e9ecef !important;
        }
      `}</style>
    </Container>
  );
};

export default TempleReports;
